import { useMemo } from "react";
import { Star, X, ChevronRight } from "lucide-react";
import { fmtSigned, signClass } from "@/lib/format";
import { usePersistedState } from "@/lib/usePersistedState";

export default function WatchlistCard({ items = [], markets = [], onSelect, onRemove }) {
  const [open, setOpen] = usePersistedState("cot_watchlist_open", false);
  const rows = useMemo(() => {
    const byCode = {};
    markets.forEach((m) => {
      byCode[m.code] = m;
    });
    return items.map((it) => ({ ...it, market: byCode[it.code] }));
  }, [items, markets]);

  return (
    <div
      className={`bg-[#09090B] transition-all duration-150 ${open ? "border border-zinc-800" : "border card-collapsed-accent"}`}
      data-testid="watchlist-card"
    >
      <div
        className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between"
        data-testid={open ? "card-header-expanded" : "card-header-collapsed"}
      >
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 hover:text-amber-term transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500"
          data-testid="watchlist-collapse"
          aria-expanded={open}
        >
          <ChevronRight size={12} strokeWidth={1.5} className={`text-zinc-600 transition-transform ${open ? "rotate-90" : ""}`} />
          <Star size={11} strokeWidth={1.5} className="text-amber-term" /> Watchlist
        </button>
        <span className="text-[11px] uppercase tracking-widest text-zinc-600" data-testid="watchlist-count">
          {rows.length} pinned
        </span>
      </div>

      {!open ? null : rows.length === 0 ? (
        <div className="px-3 py-6 text-center text-[11px] uppercase tracking-widest text-zinc-600 leading-relaxed" data-testid="watchlist-empty">
          Tap ★ on any market in the table to pin it here
        </div>
      ) : (
        <div className="divide-y divide-zinc-900" data-testid="watchlist-list">
          {rows.map((r) => (
            <div
              key={r.code}
              onClick={() => onSelect?.(r.code)}
              className="group px-3 py-2 flex items-center gap-2 hover:bg-zinc-900/50 cursor-pointer transition-colors"
              data-testid={`watchlist-row-${r.symbol}`}
            >
              <span className="text-[12px] font-semibold text-amber-term w-14 shrink-0">{r.symbol}</span>
              <span className="text-[11px] text-zinc-400 flex-1 truncate">{r.name || r.market?.name || ""}</span>
              {r.market && (
                <span className={`text-[11px] tabular-nums ${signClass(r.market.net_noncomm)}`}>
                  {fmtSigned(r.market.net_noncomm)}
                </span>
              )}
              {r.market?.cot_index_1y != null && (
                <span className="text-[11px] tabular-nums text-zinc-500 w-7 text-right">
                  {Math.round(r.market.cot_index_1y)}
                </span>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove?.(r.code);
                }}
                data-testid={`watchlist-remove-${r.symbol}`}
                title="Remove from watchlist"
                className="text-zinc-600 hover:text-neg transition-colors opacity-0 group-hover:opacity-100"
              >
                <X size={12} strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
